<template>
  <div class="main-layout">
    <!-- Mobile header with hamburger -->
    <header class="main-layout__header">
      <button
        class="main-layout__menu-btn"
        :aria-label="$t('action.open_menu')"
        @click="sidebarOpen = !sidebarOpen"
      >
        <Bars3Icon class="main-layout__menu-icon" />
      </button>
      <span class="main-layout__header-title">{{ $t('app.name') }}</span>
    </header>

    <!-- Sidebar -->
    <AppSidebar
      :is-open="sidebarOpen"
      :current-user="authStore.currentUser"
      @navigate="sidebarOpen = false"
      @logout="handleLogout"
    />

    <!-- Mobile overlay -->
    <div v-if="sidebarOpen" class="main-layout__overlay" @click="sidebarOpen = false" />

    <!-- Main content -->
    <main id="main-content" class="main-layout__content">
      <ImpersonationBanner
        v-if="impersonator"
        :user-name="impersonatedName"
        :admin-name="impersonator.name"
        :leaving="leaving"
        @exit="handleExitImpersonation"
      />
      <router-view v-slot="{ Component }">
        <component :is="Component" />
      </router-view>
    </main>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { Bars3Icon } from '@heroicons/vue/24/outline'
import AppSidebar from '@/components/organisms/AppSidebar.vue'
import ImpersonationBanner from '@/components/molecules/ImpersonationBanner.vue'
import { useAuthStore } from '@/stores/auth.store'
import { useCurrentUserStore } from '@/stores/currentUser.store'
import { useFeaturesStore } from '@/stores/features.store'

const sidebarOpen = ref(false)
const authStore = useAuthStore()
const currentUserStore = useCurrentUserStore()
const featuresStore = useFeaturesStore()
const router = useRouter()

const impersonator = computed(() => currentUserStore.user?.impersonated_by ?? null)
const impersonatedName = computed(() => {
  const u = authStore.currentUser
  return u ? `${u.first_name} ${u.last_name}`.trim() || u.email : ''
})
const leaving = ref(false)

/** Back to the admin's own session; flags are re-evaluated for the real role. */
async function handleExitImpersonation() {
  leaving.value = true
  try {
    await authStore.unhijack()
    await featuresStore.load()
    await router.push({ name: 'dashboard' })
  } finally {
    leaving.value = false
  }
}

function handleLogout() {
  authStore.logout()
  router.push({ name: 'login' })
}
</script>

<style scoped lang="scss">
.main-layout {
  min-height: 100vh;
  background-color: var(--cds-background, #f4f4f4);
}

// Mobile header - hidden on desktop
.main-layout__header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background-color: var(--people-sidebar-bg);
  color: var(--people-sidebar-text);
  position: sticky;
  top: 0;
  z-index: 8000;

  @media (min-width: 1056px) {
    display: none;
  }
}

.main-layout__menu-btn {
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.main-layout__menu-icon {
  width: 24px;
  height: 24px;
}

.main-layout__header-title {
  font-size: var(--text-size-lg);
  font-weight: var(--font-weight-semibold);
}

// Mobile overlay backdrop
.main-layout__overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 8500;

  @media (min-width: 1056px) {
    display: none;
  }
}

// Main content area
.main-layout__content {
  min-height: 100vh;
  padding: 24px;

  @media (min-width: 1056px) {
    margin-left: var(--people-sidebar-width);
    padding: 32px 40px;
  }
}
</style>
